import { useCallback, useState } from "react";
import client from "../api/client";
import endpoints from "../api/endpoints";

/**
 * Like / dislike / bookmark for one item.
 *
 * This was duplicated in Movie, MovieTitle, SerieTitle and SerieEpisode --
 * SerieEpisode went as far as mutating its props and forcing a re-render
 * through an unrelated piece of state. State lives here instead, and the
 * mutual exclusion between like and dislike is applied in one place.
 *
 * @param {"movie"|"serie"|"episode"} target
 */
const useMediaActions = (target, id, initial = {}) => {
  const [liked, setLiked] = useState(Boolean(initial.liked));
  const [disliked, setDisliked] = useState(Boolean(initial.disliked));
  const [bookmarked, setBookmarked] = useState(Boolean(initial.bookmarked));
  const [pending, setPending] = useState(false);

  // Flip optimistically, roll back if the request fails.
  const send = useCallback(async (path, apply, revert) => {
    setPending(true);
    apply();
    try {
      await client.post(path, {});
    } catch (error) {
      console.error(`Action failed for ${path}:`, error);
      revert();
    } finally {
      setPending(false);
    }
  }, []);

  const toggleLike = useCallback(() => {
    const wasLiked = liked;
    const wasDisliked = disliked;
    return send(
      endpoints.like(target, id),
      () => {
        setLiked(!wasLiked);
        if (!wasLiked) setDisliked(false);
      },
      () => {
        setLiked(wasLiked);
        setDisliked(wasDisliked);
      }
    );
  }, [send, target, id, liked, disliked]);

  const toggleDislike = useCallback(() => {
    const wasLiked = liked;
    const wasDisliked = disliked;
    return send(
      endpoints.dislike(target, id),
      () => {
        setDisliked(!wasDisliked);
        if (!wasDisliked) setLiked(false);
      },
      () => {
        setLiked(wasLiked);
        setDisliked(wasDisliked);
      }
    );
  }, [send, target, id, liked, disliked]);

  const toggleBookmark = useCallback(() => {
    const was = bookmarked;
    return send(
      endpoints.bookmark(target, id),
      () => setBookmarked(!was),
      () => setBookmarked(was)
    );
  }, [send, target, id, bookmarked]);

  return {
    liked,
    disliked,
    bookmarked,
    pending,
    toggleLike,
    toggleDislike,
    toggleBookmark,
  };
};

export default useMediaActions;
